import React from "react";

/**
 * Badge — Ocean Blue Design System
 *
 * Props:
 *  variant  : "success" | "warning" | "danger" | "info" | "primary" | "neutral"
 *  size     : "sm" | "md"
 *  dot      : boolean — prepends a colored dot indicator
 *  pill     : boolean — fully rounded (default true)
 */

const VARIANTS = {
  success: {
    base: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    dot: "bg-[#10B981]",
  },
  warning: {
    base: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
    dot: "bg-[#F59E0B]",
  },
  danger: {
    base: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
    dot: "bg-[#EF4444]",
  },
  info: {
    base: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
    dot: "bg-[#3B82F6]",
  },
  primary: {
    base: "bg-[#E8F0FE] text-[#004BB5] border-[#C2D7FA]",
    dot: "bg-[#004BB5]",
  },
  neutral: {
    base: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
    dot: "bg-[#94A3B8]",
  },
  // Solid variants for counts / tags
  "solid-primary": {
    base: "bg-[#004BB5] text-white border-[#004BB5]",
    dot: "bg-white",
  },
  "solid-success": {
    base: "bg-[#10B981] text-white border-[#10B981]",
    dot: "bg-white",
  },
  "solid-danger": {
    base: "bg-[#EF4444] text-white border-[#EF4444]",
    dot: "bg-white",
  },
};

const SIZES = {
  sm: "px-2 py-0.5 text-[10px] leading-4",
  md: "px-2.5 py-0.5 text-xs leading-5",
  lg: "px-3 py-1 text-sm leading-5",
};

const Badge = ({
  children,
  variant = "neutral",
  size = "md",
  dot = false,
  pill = true,
  className = "",
  ...props
}) => {
  const config = VARIANTS[variant] ?? VARIANTS.neutral;
  const sizeClass = SIZES[size] ?? SIZES.md;

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 font-semibold tracking-wide border whitespace-nowrap",
        config.base,
        sizeClass,
        pill ? "rounded-full" : "rounded-md",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...props}
    >
      {dot && (
        <span
          className={[
            "inline-block rounded-full flex-shrink-0",
            config.dot,
            size === "sm" ? "w-1 h-1" : "w-1.5 h-1.5",
          ].join(" ")}
        />
      )}
      {children}
    </span>
  );
};

/**
 * CountBadge — small circular numeric notification indicator
 */
export const CountBadge = ({ count, max = 99, className = "" }) => {
  const display = count > max ? `${max}+` : count;
  if (!count || count <= 0) return null;
  return (
    <span
      className={[
        "inline-flex items-center justify-center",
        "w-5 h-5 text-[10px] font-bold leading-none",
        "bg-[#EF4444] text-white rounded-full border-2 border-white",
        className,
      ].join(" ")}
    >
      {display}
    </span>
  );
};

export default Badge;
